@if(error) {
  <div class="alert alert-danger">{{ error }}</div>
}
<div class="container">
  <h1 data-automation-name="venEditor">
    @switch(editing) {
      @case(true) {
        <span>Edit VEN {{ven_id}}</span>
      }
      @case(false) {
        <span>Register a new VEN</span>
      }
    }
  </h1>
  <form [formGroup]="venForm" class="form-horizontal">
    <div class="form-group">
      <label for="venId"> Ven id: </label>
      <input id="venId" class="form-control" [formControl]="venForm.controls.ven_id" name="ven_id" type="text">
    </div>
    <div class="form-group">
      <label for="venName"> Ven name: </label>
      <input id="venName" class="form-control" type="text" [formControl]="venForm.controls.name" name="venName">
    </div>
    <div class="form-group">
      <label for="profile"> Profile: </label>
      <select id="profile" class="form-control" [formControl]="venForm.controls.profile" name="profile" type="select">
        <option data-automation-name="2.0a" value="2.0a">2.0a</option>
        <option data-automation-name="2.0b" value="2.0b">2.0b</option>
      </select>
    </div>

    <!-- Mode Selection -->
    <div class="form-group">
      <label for="mode"> Mode: </label>
      <select id="mode" class="form-control" [formControl]="venForm.controls.mode" name="mode" type="select">
        @for(option of modeOptions; track option.value) {
          <option [value]="option.value">{{ option.label }}</option>
        }
      </select>
      @if(isPushMode && venForm.get('ven_id')?.value) {
        <small class="text-muted">
          Push URL example: {{ pushUrl }}
        </small>
      }
    </div>

    <!-- Allow first TSO signal to update non-OADR-event -->
    <div class="form-group">
      <label for="allowUpdateNonOadrEvent" class="form-check-label">
        Allow first TSO signal to update non-OADR-event
      </label>
      <br/>
      <small>If checked, this VEN will be able to update an existing Flex event when an OADR signal with MN=0 arrives for the same Market Context or Group.</small>
      <br/>
      <input id="allowUpdateNonOadrEvent" [formControl]="venForm.controls.allowUpdateNonOadrEvent" class="form-check-input" name="allowUpdateNonOadrEvent" type="checkbox">

      <!-- Sub-options (indented radio group) -->
      @if(venForm.controls.allowUpdateNonOadrEvent.value) {
        <div style="margin-left: 30px; margin-top: 10px;">
          <div class="form-group">
            <label>
              <input type="radio" name="allowedToMatchPartial" [value]="false" [formControl]="venForm.controls.allowedToMatchPartial" [checked]="!venForm.controls.allowedToMatchPartial.value">
              Allow exact match with event window
            </label>
            <br/>
            <small style="margin-left: 20px;">Update instructions only if start and duration match exactly with an existing Flex event for signals where MN=0. Event End time can't be updated.</small>
          </div>
          <br/>
          <div class="form-group">
            <label>
              <input type="radio" name="allowedToMatchPartial" [value]="true" [formControl]="venForm.controls.allowedToMatchPartial" [checked]="venForm.controls.allowedToMatchPartial.value">
              Allow partial match for only overlapping intervals
            </label>
            <br/>
            <small style="margin-left: 20px;">Update instructions for overlapping intervals with an existing Flex event for signals where MN=0. OADR dispatch can have different event start or duration, as long as some intervals overlap.</small>
          </div>
        </div>
      }
    </div>
    <br/>

    <!-- Allow existing OADR Event Updates -->
    <div class="form-group">
      <label for="allowUpdateOadrEvent" class="form-check-label">
        Allow existing OADR Event Updates
      </label>
      <br/>
      <small>If checked, this VEN will be able to modify existing OADR dispatches with same OADR event ID.</small>
      <br/>
      <input id="allowUpdateOadrEvent" [formControl]="venForm.controls.allowUpdateOadrEvent" class="form-check-input" name="allowUpdateOadrEvent" type="checkbox">

      <!-- Sub-options (indented radio group) -->
      @if(venForm.controls.allowUpdateOadrEvent.value) {
        <div style="margin-left: 30px; margin-top: 10px;">
          <div class="form-group">
            <label>
              <input type="radio" name="allowEndDateUpdate" [value]="true" [formControl]="venForm.controls.allowEndDateUpdate" [checked]="venForm.controls.allowEndDateUpdate.value">
              Allow to update duration and instructions
            </label>
            <br/>
            <small style="margin-left: 20px;">Update the end time (duration) and instructions of an existing dispatch with the same OADR event ID. Event start time can not be updated in Flex.</small>
          </div>
          <br/>
          <div class="form-group">
            <label>
              <input type="radio" name="allowEndDateUpdate" [value]="false" [formControl]="venForm.controls.allowEndDateUpdate" [checked]="!venForm.controls.allowEndDateUpdate.value">
              Allow to update only instructions for overlapping intervals
            </label>
            <br/>
            <small style="margin-left: 20px;">Update instructions for overlapping intervals with an existing OADR dispatch with same event ID for signals where MN>=0. Event End time can't be updated.</small>
          </div>
        </div>
      }
    </div>
    <br/>

    <!-- Event cancellation -->
    <div class="form-group">
      <label for="cancelEnabled" class="form-check-label">
        Event cancellation
        <span class="text-muted" style="font-weight: normal;"> (Logic applies only for MN>0)</span>
      </label>
      <br/>
      <small>If checked, the VEN will cancel events based on Cancelation signal from the TSO and cancel the event in the platform.</small>
      <br/>
      <input id="cancelEnabled" class="form-check-input" [formControl]="venForm.controls.cancelEnabled" name="cancelEnabled" type="checkbox">
      @if(!venForm.controls.allowEndDateUpdate.value && venForm.controls.allowUpdateOadrEvent.value) {
        <br/>
        <small class="text-warning">Note: Event cancellation is disabled when "Allow to update only instructions for overlapping intervals" is selected.</small>
      }
    </div>
    <br/>

    <div class="form-group">
      <label for="emsCheckEnabled" class="form-check-label">
        Event EMS checking
      </label>
      <br/>
      <small>If set, all events received by the VEN will be checked against EMS</small>
      <br/>
      <input id="emsCheckEnabled" [formControl]="venForm.controls.emsCheckEnabled" class="form-check-input" name="emsCheckEnabled" type="checkbox">
    </div>
    <br/>
    <div class="form-group">
      <label for="reportEnabled" class="form-check-label">
        Reporting
      </label>
      <br/>
      <small>If set, the VEN will register Metadata Reports and allow creation of Registered Reports by VTNs.</small>
      <br/>
      <small>If not set, the VEN will reject all requests related to reporting.</small>
      <br/>
      <input id="reportEnabled" class="form-check-input" [formControl]="venForm.controls.reportEnabled" name="reportEnabled" type="checkbox">
    </div>
    <br/>
    <div class="form-group">
      <label for="registrationCancelEnabled" class="form-check-label">
        Registration cancellation
      </label>
      <br/>
      <small>If set, the VEN will notify the VTN of when cancelling a registration.</small>
      <br/>
      <small>If not set, the VEN will not send a cancellation message to the VTN while cancelling a registration.</small>
      <br/>
      <input id="registrationCancelEnabled" [formControl]="venForm.controls.registrationCancelEnabled" class="form-check-input" name="registrationCancelEnabled" type="checkbox">
    </div>
    <div class="form-group">
      <label for="sendReportCancellationEnabled" class="form-check-label">
        Send report cancellation
      </label>
      <br/>
      <small>If set, report cancellation messages should be sent (It should send a message to the VTN when a registered report is cancelled in the UI.)</small>
      <br/>
      <input id="sendReportCancellationEnabled" [formControl]="venForm.controls.sendReportCancellationEnabled" class="form-check-input" name="sendReportCancellationEnabled" type="checkbox">
    </div>
    <div class="form-group">
      <label for="includeEventObligations" class="form-check-label">
        Event Obligations
      </label>
      <br/>
      <small>If set, event obligations will be included in dispatch instructions sent to the platform.</small>
      <br/>
      <small>If not set, no obligation will be provided and the platform will calculate obligation for the event.</small>
      <br/>
      <input id="includeEventObligations" [formControl]="venForm.controls.includeEventObligations" class="form-check-input" name="includeEventObligations" type="checkbox">
    </div>
    <br/>

    <!-- VTN Selection - Mode dependent -->
    <div class="form-group">
      <label for="vtn_id"> VTN </label>
      <select id="vtn_id" class="form-control" name="vtn_id" type="select" [formControl]="venForm.controls.vtnId" [compareWith]="compareById">
        <option [ngValue]="null" disabled>Select a VTN</option>
        @for(vtnOption of vtnsSelectableFiltered; track vtnOption) {
          <option [ngValue]="vtnOption">
            {{vtnOption.vtn_id}} ({{vtnOption.id}}) - {{ (vtnOption.mode || 'poll') | titlecase }}
          </option>
        }
      </select>
      <small class="text-muted">
        Only VTNs with {{ venForm.get('mode')?.value | titlecase }} mode are shown.
        VEN and VTN modes must match for compatibility.
      </small>
    </div>

    <!-- Secondary VTN Selection (with toggle) -->
    <div class="form-group">
      <button class="btn btn-default" type="button" (click)="secondaryVtnToggle = !secondaryVtnToggle" data-toggle="collapse" data-target="#secondaryVtn" aria-expanded="false">
        <span [ngClass]="secondaryVtnToggle ? 'glyphicon glyphicon-collapse-up' : 'glyphicon glyphicon-collapse-down'" aria-hidden="true"></span> Failover VTNs
      </button>
      <div id="secondaryVtn" [ngClass]="secondaryVtnToggle ? '' : 'collapse'">
        <label for="secondary_vtn">
          <small>Optional secondary VTN to connect to in place of primary VTN.</small>
        </label>
        <select id="secondary_vtn" class="form-control" name="secondary_vtn" type="select" [formControl]="venForm.controls.secondaryVtn" [compareWith]="compareById">
          <option [ngValue]="null">Select a secondary VTN</option>
          @for(secondary_vtnoption of vtnsSelectableFiltered; track secondary_vtnoption) {
            <option [ngValue]="secondary_vtnoption">
              {{secondary_vtnoption.vtn_id}} ({{secondary_vtnoption.id}}) - {{ (secondary_vtnoption.mode || 'poll') | titlecase }}
            </option>
          }
        </select>
      </div>
    </div>

    <!-- Environment Selection -->
    <div class="form-group">
      <label for="environment"> Environment: </label>
      <select id="environment" class="form-control" name="environment" type="select" [formControl]="venForm.controls.environment">
        <option data-automation-name="Legacy" value="Legacy">Legacy</option>
        <option data-automation-name="X-Energy" value="X-Energy">X-Energy</option>
      </select>
    </div>
    <!-- Platform Selection -->
    <div class="form-group">
      <label for="platform"> Platform </label>
      <select id="platform" class="form-control" name="platform" type="select" [formControl]="venForm.controls.platform" [compareWith]="compareByEnvironment">
        <option [ngValue]="null" disabled>Select a platform</option>
        @for(platform_option of platformsSelectable; track platform_option) {
          <option [ngValue]="platform_option">{{platform_option.environment}}</option>
        }
      </select>
    </div>

    <div class="row">
      <div class="col-md-6">
        <label for="included_groups">Included Groups</label>
        <table id="included_groups" class="table table-striped">
          <thead>
          <tr>
            <th colspan="4">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextGroup" name="search" [ngModelOptions]="{standalone: true}" />
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Group ID</th>
            <th>Division</th>
          </tr>
          </thead>
          <tbody>
            @if(groupsSelected && Array.isArray(groupsSelected)) {
              @for(group of groupsSelected | searchFilterPipe : searchTextGroup : 'group_id' | paginate: groupSelectedPagination; track group) {
                <tr id="group_included_{{group.id}}" [ngClass]="'table-active'">
                  <td><button id="removeGroup" class="btn btn-primary" type="button" (click)="removeGroup(group)">Remove</button></td>
                  <td>{{group.id}}</td>
                  <td>{{group.group_id}}</td>
                  <td>{{group.division}}</td>
                </tr>
              }
            }
            @if(!groupsSelected || groupsSelected.length <= 0) {
              <tr>
                <td colspan="4" style="text-align: center;"> No group on ven.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="4"><pagination-controls id="groupSelectedPagination" (pageChange)="handlePageChange($event, groupSelectedPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>

      <div class="col-md-6">
        <label for="possible_groups">Possible Groups</label>
        <table id="possible_groups" class="table table-striped">
          <thead>
          <tr>
            <th colspan="4">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextPossibleGroup" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Group ID</th>
            <th>Division</th>
          </tr>
          </thead>
          <tbody>
            @if(displayedPossibleGroups && Array.isArray(displayedPossibleGroups)) {
              @for(group of displayedPossibleGroups | searchFilterPipe : searchTextPossibleGroup : 'group_id' | paginate: displayedPossibleGroupsPagination; track group) {
                <tr id="group_possible_{{group.id}}" [ngClass]="'table-active'">
                  <td><button id="includeGroup" class="btn btn-primary" type="button" (click)="includeGroup(group)">Include</button></td>
                  <td>{{group.id}}</td>
                  <td>{{group.group_id}}</td>
                  <td>{{group.division}}</td>
                </tr>
              }
            }
            @if(!displayedPossibleGroups || displayedPossibleGroups.length <= 0) {
              <tr>
                <td colspan="4" style="text-align: center;"> No Groups exist yet.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="4"><pagination-controls id="displayedPossibleGroupsPagination" (pageChange)="handlePageChange($event, displayedPossibleGroupsPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <label for="included_resources">Included Resources</label>
        <table id="included_resources" class="table table-striped">
          <thead>
          <tr>
            <th colspan="4">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextResources" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Resource ID</th>
            <th>Program Unit ID</th>
          </tr>
          </thead>
          <tbody>
            @if(resourcesSelected && Array.isArray(resourcesSelected)) {
              @for(resource of resourcesSelected | searchFilterPipe : searchTextResources : 'resource_id' | paginate: resourcesSelectedPagination; track resource) {
                <tr id="resource_included_{{resource.id}}" [ngClass]="'table-active'">
                  <td><button id="removeResource" class="btn btn-primary" type="button" (click)="removeResource(resource)">Remove</button></td>
                  <td>{{resource.id}}</td>
                  <td>{{resource.resource_id}}</td>
                  <td>{{resource.program_unit}}</td>
                </tr>
              }
            }
            @if(!resourcesSelected || resourcesSelected.length <= 0) {
              <tr>
                <td colspan="4" style="text-align: center;"> No resource on ven.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="4"><pagination-controls id="resourcesSelectedPagination" (pageChange)="handlePageChange($event, resourcesSelectedPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>

      <div class="col-md-6">
        <label for="possible_resources">Possible Resources</label>
        <table id="possible_resources" class="table table-striped">
          <thead>
          <tr>
            <th colspan="4">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextPossibleResources" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Resource ID</th>
            <th>Program Unit ID</th>
          </tr>
          </thead>
          <tbody>
            @if(displayedPossibleResources && Array.isArray(displayedPossibleResources)) {
              @for(resource of displayedPossibleResources | searchFilterPipe : searchTextPossibleResources : 'resource_id' | paginate: displayedPossibleResourcesPagination; track resource) {
                <tr id="resource_possible_{{resource.id}}" [ngClass]="'table-active'">
                  <td><button id="includeResource" class="btn btn-primary" type="button" (click)="includeResource(resource)">Include</button></td>
                  <td>{{resource.id}}</td>
                  <td>{{resource.resource_id}}</td>
                  <td>{{resource.program_unit}}</td>
                </tr>
              }
            }
            @if(!displayedPossibleResources || displayedPossibleResources.length <= 0) {
              <tr>
                <td colspan="4" style="text-align: center;"> No Resources exist yet.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="4"><pagination-controls id="displayedPossibleResourcesPagination" (pageChange)="handlePageChange($event, displayedPossibleResourcesPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <label for="included_market_contexts">Included Market Contexts</label>
        <table id="included_market_contexts" class="table table-striped">
          <thead>
          <tr>
            <th colspan="6">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTexMarketContext" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Market Context</th>
            <th>Market Context Label</th>
            <th>Program</th>
            <th>Operator</th>
          </tr>
          </thead>
          <tbody>
            @if(marketContextsSelected && Array.isArray(marketContextsSelected)) {
              @for(market_context of marketContextsSelected | searchFilterPipe : searchTexMarketContext : 'market_context' | paginate: marketContextSelectedPagination; track market_context) {
                <tr id="market_context_included_{{market_context.id}}" [ngClass]="'table-active'">
                  <td><button id="removeMarket" class="btn btn-primary" type="button" (click)="removeMarket(market_context)">Remove</button></td>
                  <td>{{market_context.id}}</td>
                  <td>{{market_context.market_context}}</td>
                  <td>{{market_context.label}}</td>
                  <td>{{market_context.program}}</td>
                  <td>{{market_context.operator}}</td>
                </tr>
              }
            }
            @if(!marketContextsSelected || marketContextsSelected.length <= 0) {
              <tr>
                <td colspan="6" style="text-align: center;"> No market context on ven.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="6"><pagination-controls id="marketContextSelectedPagination" (pageChange)="handlePageChange($event, marketContextSelectedPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>

      <div class="col-md-6">
        <label for="possible_market_contexts">Possible Market Contexts</label>
        <table id="possible_market_contexts" class="table table-striped">
          <thead>
          <tr>
            <th colspan="6">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextPossibleMarketContext" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Market Context</th>
            <th>Market Context Label</th>
            <th>Program</th>
            <th>Operator</th>
          </tr>
          </thead>
          <tbody>
            @if(displayedPossibleMarketContext && Array.isArray(displayedPossibleMarketContext)) {
              @for(market_context of displayedPossibleMarketContext | searchFilterPipe : searchTextPossibleMarketContext : 'market_context' | paginate: displayedPossibleMarketContextPagination; track market_context) {
                <tr id="market_context_possible_{{market_context.id}}" [ngClass]="'table-active'">
                  <td><button id="includeMarket" class="btn btn-primary" type="button" (click)="includeMarket(market_context)">Include</button></td>
                  <td>{{market_context.id}}</td>
                  <td>{{market_context.market_context}}</td>
                  <td>{{market_context.label}}</td>
                  <td>{{market_context.program}}</td>
                  <td>{{market_context.operator}}</td>
                </tr>
              }
            }
            @if(!displayedPossibleMarketContext || displayedPossibleMarketContext.length <= 0) {
              <tr>
                <td colspan="6" style="text-align: center;"> No Market Contexts exist yet.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="6"><pagination-controls id="displayedPossibleMarketContextPagination" (pageChange)="handlePageChange($event, displayedPossibleMarketContextPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <label for="included_metadata_reports">Included Metadata Reports</label>
        <table id="included_metadata_reports" class="table table-striped">
          <thead>
          <tr>
            <th colspan="4">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTexMetadataReport" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Report Specifier ID</th>
            <th>Label</th>
          </tr>
          </thead>
          <tbody>
            @if(metadataReportsSelected && Array.isArray(metadataReportsSelected)) {
              @for(metadata_report of metadataReportsSelected | searchFilterPipe : searchTexMetadataReport : 'report_specifier_id' | paginate: metadataReportSelectedPagination; track metadata_report) {
                <tr id="metadata_report_included_{{metadata_report.id}}" [ngClass]="'table-active'">
                  <td><button id="remove" class="btn btn-primary" type="button" (click)="removeMetadata(metadata_report)">Remove</button></td>
                  <td>{{metadata_report.id}}</td>
                  <td>{{metadata_report.report_specifier_id}}</td>
                  <td>{{metadata_report.label}}</td>
                </tr>
              }
            }
            @if(!metadataReportsSelected || metadataReportsSelected.length <= 0) {
              <tr>
                <td colspan="4" style="text-align: center;"> No metadata report on ven.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="4"><pagination-controls id="metadataReportSelectedPagination" (pageChange)="handlePageChange($event, metadataReportSelectedPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>

      <div class="col-md-6">
        <label for="possible_metadata_reports">Possible Metadata Reports</label>
        <table id="possible_metadata_reports" class="table table-striped">
          <thead>
          <tr>
            <th colspan="4">
              <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextPossibleMetadataReport" name="search" [ngModelOptions]="{standalone: true}"/>
            </th>
          </tr>
          <tr>
            <th></th>
            <th>ID</th>
            <th>Report Specifier ID</th>
            <th>Label</th>
          </tr>
          </thead>
          <tbody>
            @if(displayedPossibleMetadataReport && Array.isArray(displayedPossibleMetadataReport)) {
              @for(metadata_report of displayedPossibleMetadataReport | searchFilterPipe : searchTextPossibleMetadataReport : 'report_specifier_id' | paginate: displayPossibleMetadataReportPagination; track metadata_report) {
                <tr id="metadata_report_possible_{{metadata_report.id}}" [ngClass]="'table-active'">
                  <td><button id="includeMetadata" class="btn btn-primary" type="button" (click)="includeMetadata(metadata_report)">Include</button></td>
                  <td>{{metadata_report.id}}</td>
                  <td>{{metadata_report.report_specifier_id}}</td>
                  <td>{{metadata_report.label}}</td>
                </tr>
              }
            }
            @if(!displayedPossibleMetadataReport || displayedPossibleMetadataReport.length <= 0) {
              <tr>
                <td colspan="4" style="text-align: center;"> No Metadata Reports exist yet.</td>
              </tr>
            }
          </tbody>
          <tfoot>
          <tr><td class="text-center" colspan="4"><pagination-controls id="displayPossibleMetadataReportPagination" (pageChange)="handlePageChange($event, displayPossibleMetadataReportPagination)" class="my-pagination"></pagination-controls></td></tr>
          </tfoot>
        </table>
      </div>
    </div>

    <button id="save" class="btn btn-primary" type="button" (click)="save()">Save</button>
    <a class="btn" (click)="backPage('/vens', router)">Cancel</a>
  </form>
</div>
